import { Box, Stack, styled, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty, pickBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box as BoxIcon, Calendar, ChevronsRight } from 'react-feather';

// Components
import ButtonBookmarkWhite from 'src/components/common/ButtonBookmarkWhite';
import FlagCountry from 'src/components/common/FlagCountry';
import IconFeather from 'src/components/common/Icon';
import FormBookmarkNote from 'src/features/Bookmark/FormBookmarkNote';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { BookmarkTypes, IBookmarkTradeDataCreatePayload } from 'src/model/bookmark';
import { useMutateCreateBookmark, useMutateUpdateBookmarkByDataUuid } from 'src/services/mutate/useMutateBookmark';
import { useQueryGetBookmarkByDataUuid, useQueryGetDataUuid } from 'src/services/queries/useQueryBookmark';
import { useQueryGetTradeDataHSCodeDetail } from 'src/services/queries/useQueryTradeData';
import { EmptyText, FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { getCountryByCountryCode } from 'src/utils/helpers/stringHelpers';
import { ISearchGlobalTradeDataQueryParams } from 'src/utils/typings/dto/tradeData';
import { useTradeDataSearchFormState } from 'src/zustand/useTradeDataSearchForm';
// import { CheckedCreditIcon } from 'src/components/Icons';
// Utilities
import TranslateText from 'src/components/common/TranslateText';
import { TradeDataSearchBy } from 'src/features/TradeData/Common/FormSearchGlobalTrade';
import { useQueryGetHSCodeDetailAIService } from 'src/services/queries/useQueryAILabeling';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
// import { usePrinter } from 'src/hooks/usePrinter';

const TypographyWhite = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  overflow: 'hidden',
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
  boxShadow: '0px 0px 50px 0px rgba(112, 114, 176, 0.08)',
}));
const TradeDataInformation = () => {
  const [isOpenBookmarkNoteForm, setIsOpenBookmarkNoteForm] = useState(false);
  const [note, setNote] = useState<string>('');
  const [isBookmark, setIsBookmark] = useState(false);

  const theme = useTheme();
  const queryParams = useQueryParams();
  const { formValues } = useTradeDataSearchFormState();
  const {
    hs_code,
    exp_country_code,
    imp_country_code,
    start_date,
    end_date,
    exp_port_code,
    imp_port_code,
    supplier_id,
    buyer_id,
    _search_by,
  } = queryParams as ISearchGlobalTradeDataQueryParams;

  const hsCodeDetailResponse = useQueryGetTradeDataHSCodeDetail({
    code: _search_by === TradeDataSearchBy.HS_CODE ? hs_code : '',
  });

  const hsCodeAIDetailResponse = useQueryGetHSCodeDetailAIService({
    code: _search_by === TradeDataSearchBy.HS_CODE_AI ? hs_code : '',
  });

  const hsCodeDetail = {
    code:
      _search_by === TradeDataSearchBy.HS_CODE
        ? hsCodeDetailResponse.data?.data?.code
        : hsCodeAIDetailResponse.data?.data?.code,
    description:
      _search_by === TradeDataSearchBy.HS_CODE
        ? (hsCodeDetailResponse.data?.data?.english_description || hsCodeDetailResponse.data?.data?.description)
        : (hsCodeAIDetailResponse.data?.data?.english_description || hsCodeAIDetailResponse.data?.data?.description),
    type: _search_by === TradeDataSearchBy.HS_CODE ? hsCodeDetailResponse.data?.data?.type : 'HS6',
  };

  // const transactionsPaymentStatusResp = useQueryTransactionsPaymentStatus(queryParams)

  const payloadCheckBookmark = useMemo(() => {
    const payload: IBookmarkTradeDataCreatePayload = {
      hs_code,
      exp_country_code,
      imp_country_code,
      start_date,
      end_date,
      exp_port_code,
      imp_port_code,
      supplier_id,
      buyer_id,
      product_name: formValues?.hs_code?.description || hsCodeDetail?.description || '',
      exp_country_name: formValues?.export_country?.label,
      imp_country_name: formValues?.import_country?.label,
      supplier_name: formValues?.supplier?.name || undefined,
      buyer_name: formValues?.buyer?.name || undefined,
      exp_port_name: formValues?.port_of_origin?.port_name,
      imp_port_name: formValues?.port_of_delivery?.port_name,
      hs_code_description: formValues?.hs_code?.description || hsCodeDetail?.description || '',
      hs_code_type: hsCodeDetail?.type || '',
    };

    return pickBy(payload, Boolean);
  }, [formValues, queryParams, hsCodeDetail]);

  const { data: dataUuid } = useQueryGetDataUuid(payloadCheckBookmark);
  const { data: dataBookmark } = useQueryGetBookmarkByDataUuid(BookmarkTypes.TradeData, dataUuid?.data || '');
  const { mutateAsync: createBookmarkMutate, isPending: isPendingCreateBookmark } = useMutateCreateBookmark();
  const { mutateAsync: updateBookmarkMutate, isPending: isPendingUpdateBookmark } = useMutateUpdateBookmarkByDataUuid();

  useEffect(() => {
    // Fetch data bookmark done
    setIsBookmark(!isEmpty(dataBookmark?.data)); // If data bookmark is empty => This company is not marked yet.
    if (isEmpty(dataBookmark?.data)) {
      setNote('');
    } else {
      setNote(dataBookmark?.data?.note || '');
    }
  }, [dataBookmark?.data?.note]);

  const handleCloseBookmarkNoteForm = () => {
    setIsOpenBookmarkNoteForm(false);
  };

  const handleOpenBookmarkNoteForm = () => {
    setIsOpenBookmarkNoteForm(true);
  };

  const handleSubmitBookmarkNoteForm = useCallback(async () => {
    const response = isBookmark
      ? await updateBookmarkMutate({
          type: BookmarkTypes.TradeData,
          dataUuid: dataUuid?.data || '',
          body: {
            note,
          },
        })
      : await createBookmarkMutate({
          type: BookmarkTypes.TradeData,
          body: { ...payloadCheckBookmark, note },
        });

    if (response?.status_code === SUCCESS_CODE.CREATE) {
      setIsBookmark(true);
    }

    handleCloseBookmarkNoteForm();
  }, [payloadCheckBookmark, note, isBookmark, dataUuid?.data]);

  return (
    <StyledBox position="relative">
      <TranslateText
        isFetching={hsCodeAIDetailResponse.isFetching}
        buttonProps={{
          color: 'inheritWhite',
        }}
        text={`${hsCodeDetail?.code ?? EmptyText} - ${hsCodeDetail?.description ?? EmptyText}`}
        render={(text: string) => (
          <TypographyWhite align="left" variant="h5" gutterBottom>
            {text}
          </TypographyWhite>
        )}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        {!hsCodeDetail && queryParams.keyword && (
          <TypographyWhite align="left" variant="h5" gutterBottom>
            {queryParams.keyword}
            {/*{transactionsPaymentStatusResp.data?.data.bol && <CheckedCreditIcon sx={{ ml: 1, mb: 0.5, verticalAlign: 'middle' }} />}*/}
          </TypographyWhite>
        )}
        <Box>
          {/* <Tooltip title="Print">
              <IconButtonWhite onClick={() => handlePrint()}>
                <IconFeather icon={<Printer />} muiProps={{ fontSize: 'small' }} />
              </IconButtonWhite>
            </Tooltip> */}
          <ButtonBookmarkWhite
            isMarked={isBookmark}
            buttonProps={{
              onClick: () => handleOpenBookmarkNoteForm(),
            }}
          />
        </Box>
      </Stack>
      {/*<TypographyWhite display={'inline'} align="left" variant="body2" gutterBottom>*/}
      {/*  {hsCodeDetail?.description &&*/}
      {/*    `This is the result of all import and export data of ${hsCodeDetail?.description}`}*/}
      {/*  {exp_country_code &&*/}
      {/*    imp_country_code &&*/}
      {/*    `. Exported from ${getCountryByCountryCode(exp_country_code)?.label} to the ${getCountryByCountryCode(imp_country_code)?.label}`}*/}
      {/*  {start_date &&*/}
      {/*    end_date &&*/}
      {/*    ` from ${dayjs(start_date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.LONG_MONTH_DAY_YEAR)} until ${dayjs(end_date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.LONG_MONTH_DAY_YEAR)}.`}*/}
      {/*</TypographyWhite>*/}
      <Stack spacing={4} direction="row" alignItems="center">
        {hsCodeDetail && (
          <Stack spacing={0.5} direction="row" alignItems="center">
            <IconFeather icon={<BoxIcon />} muiProps={{ sx: { color: theme.palette.common.white, fontSize: 16 } }} />
            <TypographyWhite variant="caption" align="left">
              {hsCodeDetail?.code}
            </TypographyWhite>
          </Stack>
        )}
        {(exp_country_code || imp_country_code) && (
          <Stack spacing={0.5} direction="row" alignItems="center">
            {exp_country_code && (
              <Stack alignItems={'center'} spacing={0.5} direction="row">
                <FlagCountry code={exp_country_code} />
                <TypographyWhite variant="caption" align="left">
                  {getCountryByCountryCode(exp_country_code)?.label}
                </TypographyWhite>
              </Stack>
            )}

            {exp_country_code && imp_country_code && (
              <IconFeather
                icon={<ChevronsRight />}
                muiProps={{ sx: { color: theme.palette.common.white, fontSize: 16 } }}
              />
            )}
            {imp_country_code && (
              <Stack alignItems={'center'} spacing={0.5} direction="row">
                <FlagCountry code={imp_country_code} />
                <TypographyWhite variant="caption" align="left">
                  {getCountryByCountryCode(imp_country_code)?.label}
                </TypographyWhite>
              </Stack>
            )}
          </Stack>
        )}
        <Stack spacing={0.5} direction="row" alignItems="center">
          <IconFeather icon={<Calendar />} muiProps={{ sx: { color: theme.palette.common.white, fontSize: 16 } }} />
          <TypographyWhite variant="caption" align="left">
            {dayjs(start_date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE)}
          </TypographyWhite>
          <TypographyWhite variant="caption" align="left">
            -
          </TypographyWhite>
          <IconFeather icon={<Calendar />} muiProps={{ sx: { color: theme.palette.common.white, fontSize: 16 } }} />
          <TypographyWhite variant="caption" align="left">
            {dayjs(end_date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE)}
          </TypographyWhite>
        </Stack>
      </Stack>
      <FormBookmarkNote
        open={isOpenBookmarkNoteForm}
        handleClose={handleCloseBookmarkNoteForm}
        handleSubmit={() => handleSubmitBookmarkNoteForm()}
        value={note}
        onChange={(value) => setNote(value)}
        isLoading={isPendingCreateBookmark || isPendingUpdateBookmark}
      />
    </StyledBox>
  );
};

export default TradeDataInformation;
